import React from "react"
import styled, { css } from "styled-components"
import { Flex, LayoutProps } from "../Flex"
import { Icon } from "../Icon"
import { useKeyHandler, useClickOutside } from "../../hooks"
import { scale, fontWeight, colors } from "../../styles"
import { HEADER_HEIGHT_S } from "../../constants"
import { ChildrenProps } from "../../@types/@core"

type ModalNode = React.ReactNode

export interface ModalProps {
	zIndex?: number
}
export interface ModalContextValue {
	modals: ModalNode[]
	modalIds: string[]
	addModal: (modal: ModalNode) => void
	setModalIds: React.Dispatch<React.SetStateAction<string[]>>
	closeModal: () => void
}
export const ModalContext = React.createContext<ModalContextValue>({
	modals: [], // List of mounted modal elements
	addModal: () => {},
	modalIds: [], // Keeps track of visible modals for aria attr
	setModalIds: () => {},
	closeModal: () => {},
})

const useModal = () => React.useContext(ModalContext)

export interface ModalComponent {
	Provider: typeof ModalProvider
	Window: typeof ModalWindow
}

const ModalProvider: React.FC<ChildrenProps> = (props) => {
	const [modals, setModals] = React.useState<ModalContextValue["modals"]>([])
	const [modalIds, setModalIds] = React.useState<ModalContextValue["modalIds"]>(
		[]
	)
	const addModal = (modal: ModalNode) => {
		setModals((prev) => [modal, ...prev])
	}
	const closeModal = () => {
		setModals((prev) => prev.filter((val, i) => i !== 0))
	}
	React.useEffect(() => {
		modals.length > 0
			? (document.body.style.overflow = "hidden")
			: (document.body.style.overflow = "auto")
		return () => {
			document.body.style.overflow = "auto"
		}
	}, [modals])

	return (
		<ModalContext.Provider
			value={{ modals, addModal, modalIds, setModalIds, closeModal }}
		>
			{props.children}
		</ModalContext.Provider>
	)
}

interface OverlayProps {
	visible?: boolean
	zIndex?: number
}

const Overlay = styled.div<OverlayProps>`
	position: fixed;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	background: rgba(15, 15, 15, 0.3);
	visibility: hidden;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.2s, visibility 0.2s;
	z-index: ${(props) => props.zIndex};

	${(props) =>
		props.visible &&
		css`
			visibility: visible;
			opacity: 1;
			pointer-events: all;
		`}
`

const ModalWindowStyled = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	margin: auto;
	background-color: ${colors.white};
	max-width: ${scale.px(69)}; // 552px
`

const ModalWindowHeaderStyled = styled.div`
	position: sticky;
	top: 0;
	z-index: 10;
	background-color: inherit;
	padding: ${scale.px(2)} ${scale.px(2.25)};
	border-bottom: 1px solid ${colors.blue33};
	display: flex;
	justify-content: right;
	overflow: hidden;
`

const CloseBtn = styled.button`
	display: flex;
	align-items: center;
	font-size: ${scale.px(1.75)};
	line-height: ${scale.px(2)};
	text-transform: uppercase;
	font-weight: ${fontWeight.bold};

	&:hover {
		color: ${colors.hoverBlue};

		path {
			fill: ${colors.hoverBlue};
		}
	}
`

interface ModalWindowProps extends LayoutProps {
	onClose?: () => any
	id: string
	closeBtnLabel: string
	children?: React.ReactNode
}

const ModalWindow: React.FC<ModalWindowProps> = ({
	onClose,
	children,
	id,
	closeBtnLabel,
	...rest
}) => {
	const { closeModal, modalIds, setModalIds } = useModal()
	const windowRef = React.useRef() as React.MutableRefObject<HTMLDivElement>
	const closeBtnRef =
		React.useRef() as React.MutableRefObject<HTMLButtonElement>

	// Move focus to modal window
	React.useEffect(() => {
		if (closeBtnRef.current) {
			setTimeout(() => {
				closeBtnRef.current.focus()
			}, 100)
		}
	}, [closeBtnRef])

	React.useEffect(() => {
		setModalIds([...modalIds, id])

		return function () {
			setModalIds(modalIds.filter((mId) => mId === id))
		}
	}, [])

	const handleClose = () => {
		closeModal()
		onClose && onClose()
	}

	// close on esc
	useKeyHandler(27, handleClose)
	// close on click outside modal window
	useClickOutside(windowRef, handleClose)

	return (
		<ModalWindowStyled
			id={id}
			role="dialog"
			aria-hidden={modalIds.includes(id) ? "false" : "true"}
			{...rest}
		>
			<ModalWindowHeaderStyled>
				<CloseBtn ref={closeBtnRef} onClick={handleClose}>
					{closeBtnLabel} <Icon ml={1} icon="close" />
				</CloseBtn>
			</ModalWindowHeaderStyled>
			<Flex
				flexDirection="column"
				pt={{ _: 3, l: 5 }}
				pb={{ _: HEADER_HEIGHT_S + 2, l: 5 }} // In mobile we need to make sure that bottom of modal window is always reachable
				px={{ _: 3, l: 5 }}
				ref={windowRef}
				role="document"
			>
				{children}
			</Flex>
		</ModalWindowStyled>
	)
}

ModalWindow.defaultProps = {
	onClose: () => {},
}

const Modal: React.FC<ModalProps> & ModalComponent = (props) => {
	const { modals } = useModal()
	const hasModals = modals.length > 0

	return (
		<Overlay visible={hasModals} zIndex={props.zIndex}>
			<Flex
				overflow="auto"
				w={1}
				h={1}
				alignItems="center"
				justifyContent="center"
				my={3}
				mx="auto"
				position="fixed"
			>
				{hasModals ? modals[0] : null}
			</Flex>
		</Overlay>
	)
}

Modal.Provider = ModalProvider
Modal.Window = ModalWindow

export { Modal, useModal }
